import React, { useContext, useEffect } from 'react';
import { useS3ProxyApi } from 'src/hooks/useS3ProxyApi';
import { NavGraph, NavGraphItem } from 'src/types/navGraph';
import { NavGraphApiType } from 'src/types/useApi';

export const NAV_GRAPH_ROOT_NODE_ID = 21369;

export interface NavGraphContextType {
    navGraph: NavGraph;
    isLoading: boolean;
    hasError: boolean;
    getBreadcrumbNamesFromPath: (path?: string) => string[];
    getPathSegmentsByDocumentId: (
        documentId: number
    ) => { id: string; name: string }[] | undefined;
}

export const NavGraphContext = React.createContext<NavGraphContextType>({
    navGraph: {},
    isLoading: false,
    hasError: false,
    getBreadcrumbNamesFromPath: () => [],
    getPathSegmentsByDocumentId: () => undefined,
});

export const useNavGraph = () => useContext(NavGraphContext);

interface NavGraphProviderProps {
    children: React.ReactNode;
}

export const NavGraphProvider = ({ children }: NavGraphProviderProps) => {
    const { makeRequest, data, error, loading }: NavGraphApiType =
        useS3ProxyApi({
            url: 'v2/nav-graph',
            method: 'GET',
        });

    useEffect(() => {
        makeRequest();
    }, []);

    const navGraphData = React.useMemo(() => {
        if (data && data[NAV_GRAPH_ROOT_NODE_ID]) {
            return data[NAV_GRAPH_ROOT_NODE_ID].children || {};
        }
        return data;
    }, [data]);

    const getPathSegmentsByDocumentId = (documentId: number) => {
        const findDocumentPath = (
            node: NavGraphItem,
            targetId: number,
            currentPath: { id: string; name: string }[]
        ): { id: string; name: string }[] | undefined => {
            if (node.documentId === targetId) {
                return currentPath;
            }
            if (node.children) {
                for (const [childId, child] of Object.entries(node.children)) {
                    const result = findDocumentPath(child, targetId, [
                        ...currentPath,
                        { id: childId, name: child.name },
                    ]);
                    if (result) return result;
                }
            }
            return undefined;
        };

        if (!data) return undefined;

        for (const [nodeId, node] of Object.entries(data)) {
            const path = findDocumentPath(node, documentId, [
                { id: nodeId, name: node.name },
            ]);
            if (path) return path;
        }

        return undefined;
    };

    const getBreadcrumbNamesFromPath = (path?: string) => {
        const names: string[] = [];
        if (!navGraphData || !path) return names;

        const pathIds = path.split('/').filter(Boolean);
        if (
            pathIds[0] === String(NAV_GRAPH_ROOT_NODE_ID) ||
            pathIds[0] === '357'
        ) {
            pathIds.shift();
        }

        let currentNode: NavGraphItem | undefined;
        pathIds.forEach((id, index) => {
            currentNode =
                index === 0 ? navGraphData[id] : currentNode?.children?.[id];
            if (currentNode?.name) {
                names.push(currentNode.name);
            }
        });
        return names;
    };

    const value: NavGraphContextType = {
        navGraph: navGraphData,
        isLoading: loading,
        hasError: !!error,
        getBreadcrumbNamesFromPath,
        getPathSegmentsByDocumentId,
    };

    return (
        <NavGraphContext.Provider value={value}>
            {children}
        </NavGraphContext.Provider>
    );
};
